/**
 * Conteúdo da aba Clientes no relatório.
 * Exibe filtros próprios e tabela de clientes com ordenação e paginação.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  MenuItem,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  Collapse,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Person, Business, Search, Clear, FilterList, ExpandMore, ExpandLess, PictureAsPdf } from '@mui/icons-material';
import { Client } from '../../types';
import { formatarCPFCNPJ, formatarData } from '../../utils/formatToView';
import { useRelatorioCores, getStatusChip, fmtInt } from './relatorioUtils';
import { FiltersClientes, estadosBR } from './relatorioTypes';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import { parseYmdToLocalDate, dateToYmd } from './relatorioUtils';

export interface RelatorioClientesTabProps {
  data: Client[];
  filters: FiltersClientes;
  onFilterChange: (updates: Partial<FiltersClientes>) => void;
  onClearFilters: () => void;
  isLoading: boolean;
  page: number;
  rowsPerPage: number;
  orderBy: string;
  orderDir: 'asc' | 'desc';
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (col: string) => void;
  sortData: <T>(data: T[]) => T[];
  paginateData: <T>(data: T[]) => T[];
  onExportPdf?: (ids: number[]) => Promise<void>;
}

const RelatorioClientesTab: React.FC<RelatorioClientesTabProps> = ({
  data,
  filters,
  onFilterChange,
  onClearFilters,
  isLoading,
  page,
  rowsPerPage,
  orderBy,
  orderDir,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  sortData,
  paginateData,
  onExportPdf,
}) => {
  const [exportingPdf, setExportingPdf] = React.useState(false);
  const handleExportPdf = async () => {
    if (!onExportPdf || data.length === 0) return;
    setExportingPdf(true);
    try {
      await onExportPdf(data.map((c) => c.id));
    } finally {
      setExportingPdf(false);
    }
  };
  const cores = useRelatorioCores();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  const sorted = sortData(data);
  const rows = paginateData(sorted);
  const navigate = useNavigate();

  const columns = [
    { id: 'id', label: 'ID', width: 60 },
    { id: 'nome', label: 'Nome' },
    { id: 'cpf_cnpj', label: 'CPF/CNPJ', width: 150 },
    { id: 'tipo_pessoa', label: 'Tipo', width: 70 },
    { id: 'status', label: 'Status', width: 110 },
    ...(filters.inadimplente ? [{ id: 'qtde_faturas_vencidas', label: 'Qtde Faturas', width: 100 }] : []),
    { id: 'cidade', label: 'Cidade' },
    { id: 'estado', label: 'UF', width: 50 },
    { id: 'bairro', label: 'Bairro' },
    { id: 'createdAt', label: 'Cadastro', width: 100 },
  ];

  return (
    <Box>
      <Paper elevation={2} sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: alpha(cores.primario, 0.04),
            cursor: 'pointer',
          }}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ fontSize: 18, color: cores.primario }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>Filtros</Typography>
            {hasActiveFilters && (
              <Typography component="span" variant="caption" color="primary" fontWeight={600}>Filtros ativos</Typography>
            )}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.74rem' }}>
              {isLoading ? <Skeleton width={100} sx={{ display: 'inline-block' }} /> : (
                <><strong>{fmtInt(data.length)}</strong> registro(s) encontrado(s)</>
              )}
            </Typography>
          </Box>
          <IconButton size="small">{filtersOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>
        <Collapse in={filtersOpen}>
          <Box sx={{ p: 1, pt: 0.5 }}>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6} md={3}>
                <SysDateRangePicker
                  label="Data de Cadastro"
                  value={{
                    startDate: filters.dataInicio ? parseYmdToLocalDate(filters.dataInicio) : null,
                    endDate: filters.dataFim ? parseYmdToLocalDate(filters.dataFim) : null,
                  }}
                  onChange={(range) => onFilterChange({ dataInicio: dateToYmd(range.startDate), dataFim: dateToYmd(range.endDate) })}
                />
              </Grid>
              <Grid item xs={4} sm={3} md={1}>
                <TextField fullWidth size="small" select label="Estado" value={filters.estado} onChange={(e) => onFilterChange({ estado: e.target.value })}>
                  <MenuItem value="">Todos</MenuItem>
                  {estadosBR.map((uf) => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={8} sm={3} md={2}>
                <TextField fullWidth size="small" label="Cidade" value={filters.cidade} onChange={(e) => onFilterChange({ cidade: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField fullWidth size="small" label="Bairro" value={filters.bairro} onChange={(e) => onFilterChange({ bairro: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField fullWidth size="small" select label="Tipo Pessoa" value={filters.tipoPessoa} onChange={(e) => onFilterChange({ tipoPessoa: e.target.value })}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pf">Pessoa Física</MenuItem>
                  <MenuItem value="pj">Pessoa Jurídica</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField fullWidth size="small" select label="Status Cliente" value={filters.statusCliente} onChange={(e) => onFilterChange({ statusCliente: e.target.value })}>
                  <MenuItem value="">Todos</MenuItem>
                   <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="inativo">Inativo</MenuItem>
                  <MenuItem value="suspenso">Suspenso</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3} md={6}>
                <TextField
                fullWidth
                size="small"
                placeholder="Buscar por nome, CPF/CNPJ..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>
                  ),
                  endAdornment: filters.search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => onFilterChange({ search: '' })}><Clear sx={{ fontSize: 16 }} /></IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              </Grid>
              <Grid item xs={6} sm={3} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.inadimplente}
                      onChange={(e) => onFilterChange({ inadimplente: e.target.checked })}
                      color="error"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontSize: '0.8rem', color: filters.inadimplente ? 'error.main' : 'text.primary', fontWeight: filters.inadimplente ? 700 : 400 }}>Apenas Inadimplentes</Typography>}
                />
              </Grid>

              {filters.statusCliente === 'inativo' && (
                <Grid item xs={6} sm={6} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.comContratosAtivos}
                        onChange={(e) => onFilterChange({ comContratosAtivos: e.target.checked })}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Com contratos ativos</Typography>}
                  />
                </Grid>
              )}
              <Grid gap={1} item xs={12} sm={12} md={filters.statusCliente === 'inativo' ? 2 : 4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={exportingPdf ? null : <PictureAsPdf sx={{ fontSize: 16 }} />}
                  onClick={handleExportPdf}
                  disabled={isLoading || data.length === 0 || exportingPdf}
                  sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                >
                  {exportingPdf ? 'Exportando...' : 'Exportar PDF'}
                </Button>
                <Button size="small" variant="outlined" startIcon={<Clear sx={{ fontSize: 16 }} />} onClick={onClearFilters} disabled={!hasActiveFilters} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>
                  Limpar Filtros
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => `color-mix(in srgb, ${cores.primario} 12%, ${theme.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(cores.primario, 0.2)}`,
                },
              }}>
                {columns.map((col: any) => (
                  <TableCell key={col.id} sx={{ width: col.width }}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? orderDir : 'asc'}
                      onClick={() => onSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((col: any) => (
                      <TableCell key={col.id}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.disabled">Nenhum registro encontrado</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((c: any) => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => navigate(`/clients/${c.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(cores.primario, 0.03) } }}
                  >
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>#{c.id}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {c.tipo_pessoa === 'pf' ? <Person sx={{ fontSize: 14, color: 'text.secondary' }} /> : <Business sx={{ fontSize: 14, color: 'text.secondary' }} />}
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nome}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarCPFCNPJ(c.cpf_cnpj)}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{c.tipo_pessoa === 'pf' ? 'PF' : 'PJ'}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{getStatusChip(c.status, cores)}</TableCell>
                    {filters.inadimplente && (
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', textAlign: 'center', fontWeight: 'bold', color: 'error.main' }}>
                        {(c as any).qtde_faturas_vencidas || 0}
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{c.cidade}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{c.estado}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{c.bairro}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarData(c.createdAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.8,
              px: 2,
              bgcolor: alpha(cores.primario, 0.04),
              borderTop: `1px solid ${alpha(cores.primario, 0.15)}`,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_e, p) => onPageChange(p)}
              onRowsPerPageChange={(e) => { onRowsPerPageChange(parseInt(e.target.value, 10)); onPageChange(0); }}
              labelRowsPerPage="Linhas por pág:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                width: '100%',
                '.MuiTablePagination-toolbar': {
                  minHeight: 32,
                  flexWrap: 'nowrap',
                  justifyContent: 'center',
                  gap: 1,
                  p: 0,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RelatorioClientesTab;
